import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';

import { DriverSelector } from '../../units/DriverSelector';
import { useDriverRoster } from '../../../hooks/useDriverRoster';
import { useSendMessage } from '../../../hooks/useMessaging';
import { enqueueSnackbar } from '../../../redux/ducks/SnackbarDucks';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 420,
    overflow: 'hidden',
  },
  selectorSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: theme.spacing(0.5),
  },
  deliveryPreview: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  composeSection: {
    flexShrink: 0,
  },
}));

/**
 * Counts how many of the selected drivers are in-app (busy/free) vs push (others).
 * @param {Set<string>} selectedIds
 * @param {Array} rosterDrivers
 * @returns {{ inApp: number, push: number }}
 */
function countDeliveryMethods(selectedIds, rosterDrivers) {
  let inApp = 0;
  let push = 0;
  rosterDrivers.forEach((d) => {
    if (!selectedIds.has(String(d.id))) return;
    if (d.status === 'busy' || d.status === 'free') {
      inApp += 1;
    } else {
      push += 1;
    }
  });
  return { inApp, push };
}

/**
 * Modal for composing and sending a broadcast message to multiple drivers.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - onSent: () => void  — called after all messages are sent successfully
 */
export function BroadcastComposeModal({ open, onClose, onSent }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { roster, isLoading: rosterLoading } = useDriverRoster();
  const { send } = useSendMessage();

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const { inApp, push } = useMemo(
    () => countDeliveryMethods(selectedIds, roster),
    [selectedIds, roster]
  );

  const canSend =
    selectedIds.size > 0 && messageText.trim().length > 0 && !isSending;

  const handleClose = () => {
    if (isSending) return;
    onClose();
  };

  const handleExited = () => {
    setSelectedIds(new Set());
    setMessageText('');
    setIsSending(false);
  };

  const handleSend = async () => {
    const text = messageText.trim();
    if (!text || selectedIds.size === 0) return;

    setIsSending(true);
    try {
      await Promise.all(
        [...selectedIds].map((driverId) =>
          send({ driverId, text, type: 'direct' })
        )
      );
      dispatch(
        enqueueSnackbar({
          message: `Message sent to ${selectedIds.size} driver${
            selectedIds.size !== 1 ? 's' : ''
          }.`,
          options: { variant: 'success' },
        })
      );
      onSent();
      onClose();
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: 'Some messages failed to send. Please try again.',
          options: { variant: 'error' },
        })
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onExited={handleExited}
      maxWidth="sm"
      fullWidth
      aria-labelledby="broadcast-compose-title"
    >
      <DialogTitle id="broadcast-compose-title">
        Broadcast Message
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        <Box className={classes.selectorSection}>
          <Typography className={classes.sectionLabel}>
            Select drivers
          </Typography>
          <DriverSelector
            drivers={roster}
            selected={selectedIds}
            onChange={setSelectedIds}
            isLoading={rosterLoading}
          />
        </Box>

        <Divider />

        <Box className={classes.composeSection}>
          <Typography className={classes.sectionLabel}>
            Message
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            multiline
            rows={3}
            placeholder="Type your broadcast message…"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            disabled={isSending}
            fullWidth
          />

          {selectedIds.size > 0 && (
            <Typography className={classes.deliveryPreview}>
              {inApp > 0 && `${inApp} will receive in-app`}
              {inApp > 0 && push > 0 && ', '}
              {push > 0 && `${push} will receive push notification`}
              {inApp === 0 && push === 0 && 'No delivery preview available'}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={!canSend}
          startIcon={isSending && <CircularProgress size={14} />}
        >
          {isSending
            ? `Sending to ${selectedIds.size}…`
            : `Send to ${selectedIds.size || 0} driver${
                selectedIds.size !== 1 ? 's' : ''
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BroadcastComposeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSent: PropTypes.func.isRequired,
};
